<template>
  <div class="edu-page">
    <Navbar />
    <PageBanner :title="pageTitle" />
    <Breadcrumb :items="breadcrumbItems" />

    <div class="edu-layout">
      <!-- Desktop left sidebar (sticky) -->
      <aside class="edu-sidebar">
        <nav :aria-label="t('common.tocLabel')">
          <button
            v-for="section in sections"
            :key="section.id"
            class="edu-sidebar-link"
            :class="{ 'is-active': activeId === section.id }"
            @click="scrollToSection(section.id)"
          >
            {{ section.label }}
          </button>
        </nav>
      </aside>

      <!-- Mobile sticky TOC -->
      <div
        class="mobile-toc"
        role="navigation"
        :aria-label="t('common.tocLabel')"
      >
        <button
          class="toc-header"
          @click="toggleExpand"
          :aria-expanded="isExpanded"
        >
          <span class="toc-header-title">{{ t("common.tocLabel") }}</span>
          <svg
            class="toc-chevron"
            :class="{ 'is-open': isExpanded }"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="#0068A8"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="toc-body">
          <template v-if="isExpanded">
            <button
              v-for="section in sections"
              :key="section.id"
              class="toc-link"
              :class="{ 'is-active': activeId === section.id }"
              @click="collapseAndScroll(section.id)"
            >
              {{ section.label }}
            </button>
          </template>
          <template v-else>
            <button
              class="toc-link toc-current"
              @click="scrollToSection(activeId)"
            >
              {{ sections.find((s) => s.id === activeId)?.label }}
            </button>
          </template>
        </div>
      </div>

      <!-- Main content -->
      <main class="edu-content" @click="handleBodyClick">
        <template v-if="isLoading">
          <section class="edu-section">
            <p class="edu-section-text">{{ t("common.loading") }}</p>
          </section>
        </template>
        <template v-else-if="errorMessage">
          <section class="edu-section">
            <p class="edu-section-text">{{ errorMessage }}</p>
          </section>
        </template>
        <template v-else>
          <!-- Section 1: 藥品許可證字號編碼與許可證資料集 -->
          <section
            v-if="licenseCodingItem"
            id="section-license-coding"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ licenseCodingTitle }}</h2>
            <div v-for="(block, idx) in licenseCodingBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="licenseCodingItem" class="edu-divider" />

          <!-- Section 2: 藥品許可證資料的潛在問題 -->
          <section
            v-if="licenseProblemsItem"
            id="section-license-problems"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ licenseProblemsTitle }}</h2>
            <div v-for="(block, idx) in licenseProblemsBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="licenseProblemsItem" class="edu-divider" />

          <!-- Section 3: 健保藥品代碼編碼原則 -->
          <section
            v-if="nhiCodingItem"
            id="section-nhi-coding"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ nhiCodingTitle }}</h2>
            <div v-for="(block, idx) in nhiCodingBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="nhiCodingItem" class="edu-divider" />

          <!-- Section 4: 健保藥品代碼的潛在問題 -->
          <section
            v-if="nhiProblemsItem"
            id="section-nhi-problems"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ nhiProblemsTitle }}</h2>
            <div v-for="(block, idx) in nhiProblemsBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
            </div>
          </section>

          <hr v-if="nhiProblemsItem" class="edu-divider" />

          <!-- Section 5: RxNorm : 將來源資料分組為同義詞集合（稱為概念） -->
          <section
            v-if="rxnormGroupingItem"
            id="section-rxnorm-grouping"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ rxnormGroupingTitle }}</h2>
            <div v-for="(block, idx) in rxnormGroupingBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
              <div
                v-else-if="block.type === 'code'"
                class="edu-code-block"
                role="img"
                :aria-label="block.alt || 'Code Block'"
              >
                <div class="code-lines">
                  <div
                    class="code-line"
                    v-for="(line, i) in block.lines"
                    :key="i"
                  >
                    <span class="code-num">{{ i + 1 }}.</span>
                    <span class="code-text">{{ line }}</span>
                  </div>
                </div>
                <p v-if="block.caption" class="code-caption">
                  {{ block.caption }}
                </p>
              </div>
            </div>
          </section>

          <hr v-if="rxnormGroupingItem" class="edu-divider" />

          <!-- Section 6: RxNorm : 建立相關的 RxNorm 名稱和關係 -->
          <section
            v-if="rxnormRelationsItem"
            id="section-rxnorm-relations"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ rxnormRelationsTitle }}</h2>
            <div v-for="(block, idx) in rxnormRelationsBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="rxnormRelationsItem" class="edu-divider" />

          <!-- Section 7: RxNorm 關聯圖 -->
          <section
            v-if="rxnormDiagramItem"
            id="section-rxnorm-diagram"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ rxnormDiagramTitle }}</h2>
            <div v-for="(block, idx) in rxnormDiagramBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="rxnormDiagramItem" class="edu-divider" />

          <!-- Section 8: RxNorm 課程 -->
          <section
            v-if="videosItem"
            id="section-videos"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ videosTitle }}</h2>
            <p class="edu-videos-text">
              想更深入認識 RxNorm
              嗎？您可前往「臺灣智慧醫療學校」的「研討會專區」，瀏覽相關課程影片，無須註冊即可直接觀看。
            </p>
            <a
              :href="videosHref"
              target="_blank"
              rel="noopener noreferrer"
              class="edu-videos-btn"
            >
              <span class="edu-videos-btn-text">前往臺灣智慧醫療學校</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.77778 0C0.806166 0 0 0.806166 0 1.77778V14.2222C0 15.1938 0.806166 16 1.77778 16H14.2222C15.1938 16 16 15.1938 16 14.2222V8H14.2222V14.2222H1.77778V1.77778H8V0H1.77778ZM9.77778 0V1.77778H12.9653L4.70486 10.0382L5.96181 11.2951L14.2222 3.03472V6.22222H16V0H9.77778Z"
                  fill="white"
                />
              </svg>
            </a>
          </section>
        </template>
      </main>
      <!-- Lightbox -->
      <ImageLightbox
        :visible="showLightbox"
        :src="currentLightboxImage"
        :alt="pageTitle"
        @close="closeLightbox"
      />
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { useEducationToc } from "@/composables/useEducationToc";
import { contentAPI } from "@/services/api";

const { locale, t } = useI18n();

const isLoading = ref(true);
const errorMessage = ref("");
const apiItems = ref([]);

function safeJsonParse(value) {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

const localizedItems = computed(() =>
  apiItems.value
    .map((item) => {
      const contentSource =
        locale.value === "en-US" ? item.content_en : item.content;
      return { ...item, _parsed: safeJsonParse(contentSource) };
    })
    .filter((item) => item._parsed && item._parsed.id)
);

const metaItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "meta")
);
const licenseCodingItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "license-coding")
);
const licenseProblemsItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "license-problems")
);
const nhiCodingItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "nhi-coding")
);
const nhiProblemsItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "nhi-problems")
);
const rxnormGroupingItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "rxnorm-grouping")
);
const rxnormRelationsItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "rxnorm-relations")
);
const rxnormDiagramItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "rxnorm-diagram")
);
const videosItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "videos")
);

const pageTitle = computed(
  () => metaItem.value?._parsed?.bannerTitle || t("loincEducation.title")
);

const licenseCodingTitle = computed(() => {
  const item = licenseCodingItem.value;
  if (!item) return "藥品許可證字號編碼與許可證資料集";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const licenseCodingBlocks = computed(
  () => licenseCodingItem.value?._parsed?.blocks || []
);

const licenseProblemsTitle = computed(() => {
  const item = licenseProblemsItem.value;
  if (!item) return "藥品許可證資料的潛在問題";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const licenseProblemsBlocks = computed(
  () => licenseProblemsItem.value?._parsed?.blocks || []
);

const nhiCodingTitle = computed(() => {
  const item = nhiCodingItem.value;
  if (!item) return "健保藥品代碼編碼原則";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const nhiCodingBlocks = computed(
  () => nhiCodingItem.value?._parsed?.blocks || []
);

const nhiProblemsTitle = computed(() => {
  const item = nhiProblemsItem.value;
  if (!item) return "健保藥品代碼的潛在問題";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const nhiProblemsBlocks = computed(
  () => nhiProblemsItem.value?._parsed?.blocks || []
);

const rxnormGroupingTitle = computed(() => {
  const item = rxnormGroupingItem.value;
  if (!item) return "RxNorm : 將來源資料分組為同義詞集合（稱為概念）";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const rxnormGroupingBlocks = computed(
  () => rxnormGroupingItem.value?._parsed?.blocks || []
);

const rxnormRelationsTitle = computed(() => {
  const item = rxnormRelationsItem.value;
  if (!item) return "RxNorm : 建立相關的 RxNorm 名稱和關係";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const rxnormRelationsBlocks = computed(
  () => rxnormRelationsItem.value?._parsed?.blocks || []
);

const rxnormDiagramTitle = computed(() => {
  const item = rxnormDiagramItem.value;
  if (!item) return "RxNorm 關聯圖";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const rxnormDiagramBlocks = computed(
  () => rxnormDiagramItem.value?._parsed?.blocks || []
);

const videosTitle = computed(() => {
  const item = videosItem.value;
  if (!item) return t("loincEducation.videos.title");
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const videosDescription = computed(
  () => videosItem.value?._parsed?.description || ""
);
const videosCtaLabel = computed(
  () => videosItem.value?._parsed?.ctaLabel || ""
);
const videosHref = computed(
  () => videosItem.value?._parsed?.href || "https://smarthealth.mohw.gov.tw"
);
const videosCard = computed(() => videosItem.value?._parsed?.card);
const videosCards = computed(() => {
  const parsed = videosItem.value?._parsed;
  if (!parsed) return [];
  if (Array.isArray(parsed.cards)) return parsed.cards;
  if (parsed.card) return [parsed.card];
  return [];
});

const sections = computed(() => {
  const list = [];
  if (licenseCodingItem.value)
    list.push({
      id: "section-license-coding",
      label: licenseCodingTitle.value,
    });
  if (licenseProblemsItem.value)
    list.push({
      id: "section-license-problems",
      label: licenseProblemsTitle.value,
    });
  if (nhiCodingItem.value)
    list.push({ id: "section-nhi-coding", label: nhiCodingTitle.value });
  if (nhiProblemsItem.value)
    list.push({ id: "section-nhi-problems", label: nhiProblemsTitle.value });
  if (rxnormGroupingItem.value)
    list.push({
      id: "section-rxnorm-grouping",
      label: rxnormGroupingTitle.value,
    });
  if (rxnormRelationsItem.value)
    list.push({
      id: "section-rxnorm-relations",
      label: rxnormRelationsTitle.value,
    });
  if (rxnormDiagramItem.value)
    list.push({
      id: "section-rxnorm-diagram",
      label: rxnormDiagramTitle.value,
    });
  if (videosItem.value)
    list.push({ id: "section-videos", label: videosTitle.value });
  return list;
});

const SECTION_IDS = computed(() => sections.value.map((s) => s.id));

const {
  activeId,
  isExpanded,
  scrollToSection,
  toggleExpand,
  collapseAndScroll,
} = useEducationToc(SECTION_IDS);

const breadcrumbItems = computed(() => [
  { label: t("common.home"), path: "/" },
  {
    label:
      t("navbar.rxnormIntro") || t("navbar.snomedCtIntro") || "RxNorm 介紹",
  },
  {
    label:
      t("navbar.rxnormEducation") ||
      t("navbar.snomedCtEducation") ||
      "RxNorm 教育專區",
  },
]);

async function fetchEducationZone() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const res = await contentAPI.standardIntro({ section: "education_zone" });
    apiItems.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || t("common.systemError");
  } finally {
    isLoading.value = false;
  }
}

import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const { showLightbox, currentLightboxImage, closeLightbox, handleBodyClick } =
  useLightbox();

onMounted(() => {
  fetchEducationZone();
});
</script>

<style scoped lang="scss">
@use "@/styles/rich-content.scss" as rc;

$nav-h: 58px;
$sidebar-w: 260px;
$toc-bg: #dcf3f9;
$toc-link: #0068a8;
$active-color: var(--color-primary);
$section-alt-bg: #f9f9f9;
$title-color: #004a89;
$text-color: #333333;

/* ── Page shell ── */
.edu-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── Two-column layout ── */
.edu-layout {
  width: 100%;
  max-width: 1200px;
  margin: 50px auto 80px;
  display: flex;
  align-items: flex-start;
  position: relative;
}

/* ── Desktop sidebar ── */
.edu-sidebar {
  width: $sidebar-w;
  flex: 0 0 $sidebar-w;
  border-right: 1px solid #ededed;
  position: sticky;
  top: calc(#{$nav-h} + 24px);
  align-self: flex-start;
  padding-right: 0;
  max-height: calc(100vh - #{$nav-h} - 48px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cccccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999999;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 32px 0 16px;
  }
}

.edu-sidebar-link {
  display: inline-flex;
  width: fit-content;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding-bottom: 8px;
  cursor: pointer;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 32px;
  color: #696969;
  text-align: left;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: $active-color;
  }

  &.is-active {
    color: $active-color;
    border-bottom-color: $active-color;
  }
}

/* ── Mobile TOC (hidden on desktop) ── */
.mobile-toc {
  display: none;
}

/* ── Content area ── */
:deep(.edu-content) {
  @include rc.rich-content;

  flex: 1;
  min-width: 0;
}

/* ── Sections ── */
.edu-section {
  padding: 48px 64px;
  max-width: 840px;

  &--bg {
    background: transparent;
    max-width: 100%;
    padding-inline: 64px;
    position: relative;

    /* 用偽元素將灰底從元素左緣延伸至視窗右邊界
       min(0px, 600px - 50vw) 在視窗 > 1200px 時為負値，即向右超出容器 */
    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: min(0px, calc(600px - 50vw));
      background: $section-alt-bg;
      z-index: 0;
    }

    /* 確保內容在偽元素背景之上 */
    > * {
      position: relative;
      z-index: 1;
    }
  }
}

.edu-divider {
  border: none;
  border-top: 1px solid #ededed;
  margin: 0;
}

.edu-section-title {
  margin: 0 0 28px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 44px;
  letter-spacing: 0.02em;
  color: $title-color;
}

.edu-section-text {
  margin: 0 0 20px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 36px;
  color: $text-color;

  &:last-child {
    margin-bottom: 0;
  }
}

.edu-highlight {
  color: $toc-link;
}

/* ── Code block ── */
.edu-code-block {
  margin-top: 24px;
  border: 1px solid #e0ede8;
  border-radius: 4px;
  background: #f0f7f4;
  overflow: hidden;
  margin-bottom: 24px;
}

.code-lines {
  padding: 20px 24px;
  font-family: "Courier New", monospace;
  font-size: 14px;
  line-height: 22px;
  color: $text-color;
  overflow-x: auto;
}

.code-line {
  display: flex;
  gap: 12px;
}

.code-num {
  color: #888;
  min-width: 28px;
  user-select: none;
}

.code-caption {
  padding: 8px 24px;
  border-top: 1px solid #e0ede8;
  font-size: 13px;
  color: #666;
  text-align: right;
  margin: 0;
}

/* ── Image container ── */
.edu-image-container {
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .image-caption {
    font-size: 14px;
    color: #666;
    margin-top: 4px;
    font-style: italic;
  }
}

/* ── Video Section Custom Styling ── */
.edu-videos-text {
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 36px;
  letter-spacing: 0%;
  color: $text-color;
  margin-bottom: 28px;
  vertical-align: middle;
}

a.edu-videos-btn,
.edu-videos-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  opacity: 1;
  padding: 8px 32px;
  border-radius: 100px;
  background: $active-color;
  color: #ffffff !important;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0%;
  text-decoration: none !important;
  vertical-align: middle;
  transition: background 0.2s;

  &,
  &:hover,
  &:visited,
  &:active,
  &:focus {
    color: #ffffff !important;
  }

  .edu-videos-btn-text {
    color: #ffffff !important;
  }

  &:hover {
    background: #005f96;
  }

  svg {
    flex-shrink: 0;
    path {
      fill: #ffffff;
    }
  }
}

/* ── CTA button ── */
.edu-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  padding: 8px 32px;
  border-radius: 100px;
  background: $active-color;
  color: #fff;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #005f96;
  }
}

/* ── Mobile: ≤960px ── */
@media (max-width: 960px) {
  .edu-layout {
    flex-direction: column;
    margin: 0 0 40px;
    max-width: 100%;
  }

  .edu-sidebar {
    display: none;
  }

  /* Mobile TOC becomes a sticky accordion */
  .mobile-toc {
    display: block;
    width: 100%;
    position: sticky;
    top: $nav-h;
    z-index: 200;
    background: $toc-bg;
    border-bottom: 1px solid #b5dff0;
  }

  .toc-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    background: none;
    border: none;
    cursor: pointer;
    gap: 8px;
  }

  .toc-header-title {
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: $toc-link;
  }

  .toc-chevron {
    flex-shrink: 0;
    transition: transform 0.25s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .toc-body {
    display: flex;
    flex-direction: column;
  }

  .toc-link {
    display: block;
    width: 100%;
    padding: 10px 20px 10px 28px;
    background: none;
    border: none;
    border-left: 3px solid transparent;
    cursor: pointer;
    text-align: left;
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: $toc-link;
    transition: border-color 0.2s, background 0.2s;

    &.is-active,
    &.toc-current {
      border-left-color: $toc-link;
      background: rgba(0, 104, 168, 0.06);
    }

    &:hover {
      background: rgba(0, 104, 168, 0.1);
    }
  }

  .edu-section {
    padding: 36px 20px;
    max-width: 100%;

    &--bg {
      padding-inline: 20px;
    }
  }

  .edu-section-title {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 20px;
  }

  .edu-section-text {
    font-size: 16px;
    line-height: 28px;
  }

  .edu-cta-btn {
    width: 100%;
    max-width: 320px;
    justify-content: center;
    font-size: 18px;
  }

  /* Mobile video section responsive overrides */
  .edu-videos-text {
    font-size: 16px;
    line-height: 28px;
  }

  .edu-videos-btn {
    width: 100%;
    max-width: 320px;
    justify-content: center;
  }
}

@media (max-width: 400px) {
  .toc-header-title {
    font-size: 14px;
  }

  .edu-section-title {
    font-size: 22px;
  }
}

:deep(.edu-content img) {
  cursor: zoom-in;
}
</style>
